import Content from '@shared/types/enums/content';
import Direction from '@shared/types/enums/direction';
import Event from '@shared/classes/event';
import FastEvent from '@shared/classes/fastEvent';
import Main from '@shared/types/enums/main';
import Rail from '@shared/classes/rail';
import Slider from '@shared/classes/slider';
import TranslationManager from '@shared/assets/locale/translationsManager';
import info from '@shared/api/graphQL/sections/info';
import parser from '@shared/api/graphQL/parser';
import query from '@shared/api/graphQL/sections/query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { IHighlightResponse } from '@shared/types/interfaces/slider';
import { IReelResponse } from '@shared/types/interfaces/rail';
import { SectionQueryParams } from '@shared/types/interfaces/query';
import { routerSelector } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';

/**
 * Pages min and max size constants
 */
const MIN_HIGHLIGHTS = 4;
const MIN_SHOWS = 10;

/**
 * Pages size constants
 */
const HIGHLIGHT_PAGE_SIZE = 5;
const HORIZONTAL_REEL_PAGE_SIZE = 30;
const VERTICAL_REEL_PAGE_SIZE = 5;

/**
 * This enum defines the possible pagination strategies
 */
enum PaginationStrategy {
  FETCH_MORE = 'FETCH_MORE', // Fetch the entire collection in one query
  PAGINATED = 'PAGINATED', // Default: normal pagination
  NONE = 'NONE', // No pagination logic
}
export { PaginationStrategy };

/**
 * This interface defines the properties a graphql query request can receive
 */
interface Props {
  ready?: boolean;
  section: string;
  customPageIndex?: {
    highlights?: number;
    reels?: {
      horizontal?: number;
      vertical?: number;
    };
  };
  customPageSize?: {
    highlights?: number;
    reels?: {
      horizontal?: number;
      vertical?: number;
    };
  };
  paginationStrategy?: PaginationStrategy;
}

/**
 * Section hook. Fetches and returns highlights and reels data.
 */
export default function useSection({
  ready = true,
  section,
  customPageIndex,
  customPageSize,
  paginationStrategy = PaginationStrategy.PAGINATED,
}: Props): {
  /**
   * Whether the request is complete or not
   */
  complete: boolean;
  /**
   * Obtained highlights array
   */
  highlights: Slider[];
  /**
   * Obtained reels array
   */
  reels: Rail[];
  /**
   * Horizontal and vertical pagination callback
   */
  onPagination: (type: Main, direction: Direction) => void;
} {
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);
  const [complete, setComplete] = useState(
    !!currentParams?.rails || !!currentParams?.sliders || false
  );
  const [highlights, setHightlights] = useState(
    (currentParams as { sliders: Slider[] })?.sliders || []
  );
  const [reels, setReels] = useState(
    (currentParams as { rails: Rail[] })?.rails || []
  );
  const client = useApolloClient();
  const pagination = useRef({
    highlightPage: 0,
    horizontalPages: new Map<number, number>(),
    verticalPage: 0,
    totalHighlightPages: 0,
    totalVerticalReelPages: 0,
  });
  const populated = useRef<number[]>([]);

  // Memoized params

  const params = useMemo(() => {
    const params: SectionQueryParams = {
      highlightsHorizontalParams: {
        page: customPageIndex?.highlights || 0,
        pageSize: customPageSize?.highlights || HIGHLIGHT_PAGE_SIZE,
      },
      reelsHorizontalParams: {
        page: customPageIndex?.reels?.horizontal || 0,
        pageSize:
          customPageSize?.reels?.horizontal || HORIZONTAL_REEL_PAGE_SIZE,
      },
      reelsVerticalParams: {
        page: customPageIndex?.reels?.vertical || 0,
        pageSize: customPageSize?.reels?.vertical || VERTICAL_REEL_PAGE_SIZE,
      },
    };

    return params;
  }, [customPageIndex, customPageSize]);

  // Event handlers

  /**
   * Request completion callback.
   */
  const onCompleted = useCallback(() => {
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Request error callback.
   */
  const onError = useCallback((error: ApolloError) => {
    // Print error
    console.error(error);
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Handles horizontal pagination for the specified content type.
   * Updates query variables to trigger data fetching in the desired direction.
   *
   * @param {Main} type - The specific content type to paginate.
   * @param {Direction} direction - The pagination direction.
   */
  const onPagination = useCallback(
    (type: Main, direction?: Direction, rail?: Rail) => {
      if (paginationStrategy !== PaginationStrategy.PAGINATED) {
        return;
      }
      /**
       * Set query variables so that new data is fetched
       */
      let newVariables: SectionQueryParams = {};
      switch (type) {
        case Main.RAILS:
          // Horizontal reel displacement
          if (direction === Direction.HORIZONTAL && rail?.pagination) {
            const currentPage = rail.pagination.currentPage;
            const nextPage = currentPage + 1;
            if (
              nextPage !== pagination.current.highlightPage &&
              nextPage < rail.pagination.totalPages
            ) {
              newVariables = {
                reelsHorizontalParams: {
                  page: nextPage,
                  pageSize:
                    customPageSize?.reels?.horizontal ||
                    HORIZONTAL_REEL_PAGE_SIZE,
                },
                reelVerticalIndex: rail.pagination.verticalIndex,
              };

              // Save new horizontal page for parser
              rail.pagination.currentPage = nextPage;
              pagination.current.horizontalPages.set(
                rail.pagination.verticalIndex,
                rail.pagination.currentPage
              );
            }
            // Halt callback
            else {
              return;
            }
            break;
          }
          // Vertical reel displacement
          else if (direction === Direction.VERTICAL) {
            const currentPage = pagination.current.verticalPage;
            const nextPage = currentPage + 1;
            const totalPages = pagination.current.totalVerticalReelPages;
            if (nextPage < totalPages) {
              pagination.current.verticalPage = nextPage;
              newVariables = {
                reelsVerticalParams: {
                  page: nextPage,
                  pageSize:
                    customPageSize?.reels?.vertical || VERTICAL_REEL_PAGE_SIZE,
                },
              };
            }
            // Halt callback
            else {
              return;
            }
          }
          break;
        case Main.SLIDERS:
          // Horizontal highlight displacement
          const currentPage = pagination.current.highlightPage;
          const nextPage = currentPage + 1;
          const totalPages = pagination.current.totalHighlightPages;
          if (nextPage < totalPages) {
            pagination.current.highlightPage = Math.min(nextPage, totalPages);
            newVariables = {
              highlightsHorizontalParams: {
                page: pagination.current.highlightPage,
                pageSize: HIGHLIGHT_PAGE_SIZE,
              },
            };
          }
          // Halt callback
          else {
            return;
          }
          break;
      }

      /**
       * Fetches the next set of results for a paginated list field
       */
      const data = client.readQuery({ query, variables: newVariables });
      if (!data) {
        fetchMore({ variables: newVariables });
      }
    },
    []
  );

  // Logic

  /**
   * Parses a given response and updates main contents
   */
  const updateContents = useCallback(
    (response: { highlights?: IHighlightResponse; reels?: IReelResponse }) => {
      try {
        // Parse highlights and reels
        const highlights = parser.highlights(response?.highlights);
        const reels = parser.reels(
          response?.reels,
          pagination.current.horizontalPages
        );

        if (highlights) {
          // Update highlights
          setHightlights((state) => highlights?.data || state);
          pagination.current = {
            ...pagination.current,
            totalHighlightPages: highlights?.pages || 0,
          };

          // Populate highlights if required
          const shouldPopulate =
            (highlights?.data?.length || 0) < MIN_HIGHLIGHTS;
          shouldPopulate && onPagination(Main.SLIDERS);
        }

        if (reels) {
          // Update reels
          setReels((state) => {
            const reelData = (reels?.data || state).filter(
              (reel) => reel.data.length
            );
            if (paginationStrategy === PaginationStrategy.FETCH_MORE) {
              reelData.forEach((reel) => {
                const item = new Event({
                  _id: reel.id + '_view_more',
                  actors: [],
                  beginTime: '',
                  channel: '',
                  channelName: '',
                  countries: [],
                  country: [],
                  description: TranslationManager.get(
                    'MISC.CARDS.VIEW_MORE.DESCRIPTION'
                  ),
                  directors: [],
                  eventId: '',
                  endTime: '',
                  gender: '',
                  hosts: [],
                  moviePictures: { poster: '', photo: '', background: '' },
                  title: TranslationManager.get('MISC.CARDS.VIEW_MORE.TITLE'),
                  type: Content.VIEW_MORE,
                  writers: [],
                  year: '',
                });
                reel.data.push(item as FastEvent);
              });
            }
            return reelData;
          });

          // Update pagination data
          pagination.current.totalVerticalReelPages =
            response.reels?.totalPages || 0;

          // Populate reels if required
          const reelsToPopulate = reels?.data
            ?.map((reel) => {
              if (
                reel.pagination &&
                !populated.current.includes(reel.pagination.verticalIndex) &&
                reel.data.length < MIN_SHOWS &&
                (reel.pagination?.currentPage || 0) <
                  (reel.pagination?.totalPages ||
                    reel?.pagination?.currentPage ||
                    0)
              ) {
                populated.current.push(reel.pagination.verticalIndex);
                return reel;
              }
            })
            .filter((items) => !!items);

          if (reelsToPopulate?.length) {
            switch (paginationStrategy) {
              case PaginationStrategy.PAGINATED:
                reelsToPopulate.forEach((reel) => {
                  onPagination(Main.RAILS, Direction.HORIZONTAL, reel);
                });
                break;
            }
          }
        }
      } catch (e) {
        // Print error
        console.error(e);
      }
    },
    []
  );

  /**
   * Fetch movies highlights and reels via graphql using the query hook
   */
  const { data, fetchMore } = useQuery(query, {
    skip: !ready,
    variables: {
      ...info(section),
      ...params,
    },
    onCompleted,
    onError,
  });

  /**
   * Updates data on change
   */
  useEffect(() => {
    updateContents({
      reels: data?.reels,
      highlights: data?.highlights,
    });
  }, [data]);

  return {
    complete,
    highlights,
    reels,
    onPagination,
  };
}
